import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { PieChart, Pie, Cell, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Ticket, Clock3, CheckCircle2, AlertTriangle, Star, Plus } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, StatCard, EmptyState } from "@/components/admin/ui";
import { formatHours, formatDays } from "./supportMeta";
import { TicketKanbanBoard } from "./TicketKanbanBoard";
import { CustomerContextPanel } from "./CustomerContextPanel";
import { TicketDetailDrawer } from "./TicketDetailDrawer";

const PIE_COLORS = ["#0052FF", "#7C3AED", "#F59E0B", "#10B981", "#EF4444", "#06B6D4", "#64748B"];

const ChangePct = ({ value }) => {
  if (value == null) return <span className="text-xs text-slate-400">No prior period to compare</span>;
  const up = value >= 0;
  return <span className={`text-xs font-bold ${up ? "text-emerald-600 dark:text-emerald-400" : "text-red-500"}`}>{up ? "↑" : "↓"} {Math.abs(value)}% vs previous period</span>;
};

export const SupportOverviewTab = ({ API, meta, canManage, onNewTicket, refreshKey }) => {
  const [rangeDays, setRangeDays] = useState(30);
  const [overview, setOverview] = useState(null);
  const [board, setBoard] = useState(null);
  const [error, setError] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedTicketId, setSelectedTicketId] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/org/support/overview`, { params: { range_days: rangeDays }, withCredentials: true }).then(({ data }) => setOverview(data)).catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
    axios.get(`${API}/org/support/tickets/board`, { withCredentials: true }).then(({ data }) => setBoard(data.columns)).catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, rangeDays]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const openTicket = (t) => { setSelectedTicketId(t.ticket_id); setSelectedCustomerId(t.customer_id); };

  if (error) return <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="support-overview-error">{error}</p>;
  if (!overview || !board) return <p className="text-sm text-slate-400" data-testid="support-overview-loading">Loading…</p>;

  const k = overview.kpis;

  return (
    <div className="space-y-6" data-testid="support-overview-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-extrabold text-slate-950 dark:text-white">Support Insights</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">Real-time view of your Support operation.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={rangeDays} onChange={(e) => setRangeDays(Number(e.target.value))} data-testid="support-overview-range-select" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            <option value={7}>Last 7 Days</option>
            <option value={30}>Last 30 Days</option>
            <option value={90}>Last 90 Days</option>
          </select>
          {canManage && (
            <button type="button" onClick={() => onNewTicket()} data-testid="support-overview-new-ticket-btn" className="flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC]">
              <Plus size={14} /> New Ticket
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard icon={Ticket} tone="blue" label="Open Tickets" value={k.open_tickets.value} helper={<ChangePct value={k.open_tickets.change_pct} />} testId="support-kpi-open-tickets" />
        <StatCard icon={Clock3} tone="purple" label="Avg First Response" value={k.avg_first_response_hours.has_data ? formatHours(k.avg_first_response_hours.value) : "—"} helper={k.avg_first_response_hours.has_data ? <ChangePct value={k.avg_first_response_hours.change_pct} /> : "No responses yet"} testId="support-kpi-first-response" />
        <StatCard icon={CheckCircle2} tone="green" label="Avg Resolution Time" value={k.avg_resolution_days.has_data ? formatDays(k.avg_resolution_days.value) : "—"} helper={k.avg_resolution_days.has_data ? <ChangePct value={k.avg_resolution_days.change_pct} /> : "No resolved tickets yet"} testId="support-kpi-resolution-time" />
        <StatCard icon={AlertTriangle} tone="orange" label="Escalated Tickets" value={k.escalated_tickets.value} helper={<ChangePct value={k.escalated_tickets.change_pct} />} testId="support-kpi-escalated" />
        <StatCard icon={Star} tone="blue" label="Customer Rating" value={k.customer_rating.has_data ? `${k.customer_rating.value}/5` : "—"} helper={k.customer_rating.has_data ? <ChangePct value={k.customer_rating.change_pct} /> : "No feedback yet"} testId="support-kpi-rating" />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Panel title="Tickets by Type" testId="support-chart-by-type">
          {!overview.tickets_by_type.has_data ? <EmptyState icon={Ticket} title="No ticket data yet." message="Create a few Tickets to see the breakdown here." testId="support-chart-by-type-empty" /> : (
            <div data-testid="support-chart-by-type-chart">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={overview.tickets_by_type.breakdown} dataKey="count" nameKey="category" innerRadius={45} outerRadius={75} paddingAngle={2}>
                      {overview.tickets_by_type.breakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v, n, p) => [`${v} (${p.payload.pct}%)`, p.payload.category]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1" data-testid="support-chart-by-type-legend">
                {overview.tickets_by_type.breakdown.map((b, i) => (
                  <span key={b.category} className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                    <span className="h-2 w-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                    {b.category} ({b.count})
                  </span>
                ))}
              </div>
            </div>
          )}
        </Panel>

        <Panel title="Ticket Trend" testId="support-chart-trend">
          {!overview.ticket_trend.has_data ? <EmptyState icon={Ticket} title="No trend data yet." message="Daily ticket volume will chart here." testId="support-chart-trend-empty" /> : (
            <div className="h-56" data-testid="support-chart-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={overview.ticket_trend.points}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-slate-100 dark:stroke-white/5" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" name="Tickets" stroke="#0052FF" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>

        <Panel title="Customer Satisfaction" testId="support-chart-satisfaction">
          {!overview.customer_satisfaction.has_data ? <EmptyState icon={Star} title="No feedback yet." message="Ratings from customers will chart here." testId="support-chart-satisfaction-empty" /> : (
            <div className="h-56" data-testid="support-chart-satisfaction-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={overview.customer_satisfaction.breakdown} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} className="stroke-slate-100 dark:stroke-white/5" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="rating" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}★`} width={30} />
                  <Tooltip formatter={(v, n, p) => [`${v} (${p.payload.pct}%)`, "Responses"]} />
                  <Bar dataKey="count" fill="#0052FF" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2">
          <Panel title="Resolution Board" subtitle="Click a card to view and update its status." testId="support-resolution-board">
            <TicketKanbanBoard columns={board} onSelectTicket={openTicket} />
          </Panel>
        </div>
        <div className="min-w-0">
          {selectedCustomerId ? (
            <CustomerContextPanel API={API} customerId={selectedCustomerId} onClose={() => setSelectedCustomerId(null)} onNewTicket={onNewTicket} onSelectTicket={(t) => setSelectedTicketId(t.ticket_id)} />
          ) : (
            <div className="flex h-full min-h-[200px] items-center justify-center rounded-2xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400 dark:border-white/10" data-testid="support-context-placeholder">
              Select a ticket to see Customer context here.
            </div>
          )}
        </div>
      </div>

      {selectedTicketId && (
        <TicketDetailDrawer API={API} ticketId={selectedTicketId} meta={meta} canManage={canManage} onClose={() => setSelectedTicketId(null)} onChanged={load} />
      )}
    </div>
  );
};
